import { toSafeString } from '../utils/toSafeString.js';
const CIRRA_THREAD_RUN_PROTOCOL_VERSION = 'cirra.thread.run.v2';

const CIRRA_THREAD_RUN_PROTOCOL_METHOD = Object.freeze({
  assistantDelta: 'assistant.delta',
  contextUsage: 'context.usage',
  eventAppended: 'event.appended',
  eventUpsert: 'event.upsert',
  runDone: 'run.done',
  runMetricsToolPlanes: 'run.metrics.tool_planes',
  runPolicyConstraintsMismatch: 'run.policy_constraints.mismatch',
  runShadowAssignment: 'run.shadow.assignment',
  runShadowDiscrepancy: 'run.shadow.discrepancy',
  runState: 'run.state',
  runToolInvocationLimit: 'run.tool_invocation_limit',
  runWebSearch: 'run.web_search',
  streamInitialized: 'stream.initialized',
  streamError: 'stream.error',
  threadUpdated: 'thread.updated',
  threadsChanged: 'threads.changed',
  workflowUpdated: 'workflow.updated',
  workflowTestUpdated: 'workflow.test.updated'
});

const CIRRA_THREAD_RUN_PROTOCOL_METHODS = Object.freeze(Object.values(CIRRA_THREAD_RUN_PROTOCOL_METHOD));

const CIRRA_THREAD_RUN_CONTROL_METHOD = Object.freeze({
  turnSteer: 'turn/steer',
  turnInterrupt: 'turn/interrupt',
  turnApprovalRespond: 'turn/approval/respond',
  turnInteractionRespond: 'turn/interaction/respond'
});

const CIRRA_THREAD_RUN_CONTROL_METHODS = Object.freeze(Object.values(CIRRA_THREAD_RUN_CONTROL_METHOD));

const CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE = Object.freeze({
  string: 'string',
  boolean: 'boolean',
  object: 'object'
});

const CIRRA_THREAD_RUN_CONTROL_METHOD_PARAM_SCHEMA = Object.freeze({
  [CIRRA_THREAD_RUN_CONTROL_METHOD.turnSteer]: Object.freeze([
    { key: 'expectedRunId', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.string, required: true },
    { key: 'input', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.string, required: true },
    { key: 'idempotencyKey', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.string, required: false }
  ]),
  [CIRRA_THREAD_RUN_CONTROL_METHOD.turnInterrupt]: Object.freeze([
    { key: 'idempotencyKey', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.string, required: false },
    { key: 'reason', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.string, required: false }
  ]),
  [CIRRA_THREAD_RUN_CONTROL_METHOD.turnApprovalRespond]: Object.freeze([
    { key: 'decision', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.boolean, required: true },
    { key: 'remember', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.boolean, required: false },
    { key: 'idempotencyKey', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.string, required: false }
  ]),
  [CIRRA_THREAD_RUN_CONTROL_METHOD.turnInteractionRespond]: Object.freeze([
    { key: 'response', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.object, required: true },
    { key: 'idempotencyKey', type: CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE.string, required: false }
  ])
});

const toObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
};

const toIsoOrNow = (value) => {
  const candidate = toSafeString(value);

  if (!candidate) {
    return new Date().toISOString();
  }

  const parsed = Date.parse(candidate);

  if (!Number.isFinite(parsed)) {
    return new Date().toISOString();
  }

  return new Date(parsed).toISOString();
};

const normalizeMethod = (value) => {
  const candidate = toSafeString(value);

  if (!candidate) {
    return null;
  }

  if (!CIRRA_THREAD_RUN_PROTOCOL_METHODS.includes(candidate)) {
    return null;
  }

  return candidate;
};

const resolveParamsObject = (value) => {
  if (value === undefined) {
    return {};
  }

  const safeObject = toObject(value);

  if (!safeObject) {
    return null;
  }

  return safeObject;
};

const buildCirraThreadRunProtocolEnvelope = ({ method, params, eventId, threadId, runId, emittedAt, protocolVersion = CIRRA_THREAD_RUN_PROTOCOL_VERSION } = {}) => {
  const normalizedMethod = normalizeMethod(method);
  const paramsObject = resolveParamsObject(params);
  const normalizedRunId = toSafeString(runId);

  if (!normalizedMethod) {
    return {
      success: false,
      message: 'method is invalid'
    };
  }

  if (paramsObject === null) {
    return {
      success: false,
      message: 'params must be an object when provided'
    };
  }

  if (!normalizedRunId) {
    return {
      success: false,
      message: 'runId is required'
    };
  }

  const normalizedEventId = toSafeString(eventId) || null;
  const normalizedThreadId = toSafeString(threadId || paramsObject?.threadId) || null;
  const normalizedProtocolVersion = toSafeString(protocolVersion) || CIRRA_THREAD_RUN_PROTOCOL_VERSION;
  const normalizedEmittedAt = toIsoOrNow(emittedAt);

  return {
    success: true,
    data: {
      protocolVersion: normalizedProtocolVersion,
      method: normalizedMethod,
      params: paramsObject,
      eventId: normalizedEventId,
      threadId: normalizedThreadId,
      runId: normalizedRunId,
      emittedAt: normalizedEmittedAt,
      id: normalizedEventId,
      eventType: normalizedMethod,
      data: paramsObject,
      createdAt: normalizedEmittedAt
    }
  };
};

const normalizeCirraThreadRunProtocolEnvelope = ({ entry, runId, defaultProtocolVersion = CIRRA_THREAD_RUN_PROTOCOL_VERSION } = {}) => {
  const safeEntry = toObject(entry);

  if (!safeEntry) {
    return {
      success: false,
      message: 'entry must be an object'
    };
  }

  return buildCirraThreadRunProtocolEnvelope({
    method: safeEntry.method || safeEntry.eventType,
    params: safeEntry.params === undefined ? safeEntry.data : safeEntry.params,
    eventId: safeEntry.eventId || safeEntry.id,
    threadId: safeEntry.threadId,
    runId: safeEntry.runId || runId || safeEntry?.data?.runId || null,
    emittedAt: safeEntry.emittedAt || safeEntry.createdAt,
    protocolVersion: safeEntry.protocolVersion || defaultProtocolVersion
  });
};

export { CIRRA_THREAD_RUN_PROTOCOL_VERSION, CIRRA_THREAD_RUN_PROTOCOL_METHOD, CIRRA_THREAD_RUN_PROTOCOL_METHODS, CIRRA_THREAD_RUN_CONTROL_METHOD, CIRRA_THREAD_RUN_CONTROL_METHODS, CIRRA_THREAD_RUN_CONTROL_PARAM_TYPE, CIRRA_THREAD_RUN_CONTROL_METHOD_PARAM_SCHEMA, buildCirraThreadRunProtocolEnvelope, normalizeCirraThreadRunProtocolEnvelope };
